import {
  LogoutOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { history, useModel,useIntl } from '@umijs/max';
import type { MenuProps } from 'antd';
import { Spin, Modal, Form, Input, message } from 'antd';
import { createStyles } from 'antd-style';
import React, { useState } from 'react';
import { flushSync } from 'react-dom';
import { outLogin, changePassword } from '@/services/ant-design-pro/api';
import HeaderDropdown from '../HeaderDropdown';
import NotificationCenter from '@/pages/NotificationManagement/NotificationCenter';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState || {};
  return <span className="anticon">{currentUser?.name}</span>;
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
  menu,
  children,
}) => {
  /**
   * 退出登录，并且将当前的 url 保存
   */
  const loginOut = async () => {
    try {
      await outLogin();
    } catch (e) {
      // ignore
    }
    const { search, pathname } = window.location;
    const urlParams = new URL(window.location.href).searchParams;
    const searchParams = new URLSearchParams({
      redirect: pathname + search,
    });
    /** 此方法会跳转到 redirect 参数所在的位置 */
    const redirect = urlParams.get('redirect');
    // Note: There may be security issues, please note
    if (window.location.pathname !== '/user/login' && !redirect) {
      history.replace({
        pathname: '/user/login',
        search: searchParams.toString(),
      });
    }
  };

  const { styles } = useStyles();

  const { initialState, setInitialState } = useModel('@@initialState');

  const [visible, setVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [form] = Form.useForm();
  const intl = useIntl();
  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === 'logout') {
      flushSync(() => {
        setInitialState((s) => ({ ...s, currentUser: undefined }));
      });
      loginOut();
      return;
    }

    if (key === 'changePassword') {
      // 打开修改密码弹窗
      setVisible(true);
      return;
    }

    history.push(`/account/${key}`);
  };

  const handleCancel = () => {
    setVisible(false);
    form.resetFields();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const { oldPassword, newPassword } = values;
      setConfirmLoading(true);

      // 如果 services.changePassword 已改为接受 body 并把 data 放到请求体（推荐）
      // await changePassword({ oldPassword, newPassword });

      // 请改为 changePassword({ data: { oldPassword, newPassword } })
      
        // 如果 changePassword 返回了响应对象（未被 errorThrower 抛出），这里可以检查 res
      // 具体字段名取决于后端 ApiResponse 结构，我假设后端返回形如 { success, message, ... }
      const res = await changePassword({ oldPassword, newPassword });
  
      if (res?.status==="ok") {
        message.success('密码修改成功。');
        
        // 修改成功后强制登出并跳转到登录页（保留你原有流程）
        flushSync(() => {
          setInitialState((s) => ({ ...s, currentUser: undefined }));
        });
        try {
          await outLogin();
        } catch (e) {
          // ignore
        }
        history.replace({ pathname: '/user/login' });

        // 关闭弹窗并重置表单
        setVisible(false);
        form.resetFields();
        return;
      }
      message.error(res?.message|| '修改密码失败');

    }catch (err: any) {
      // 区分表单校验错误（err.errorFields） vs 业务错误（BizError） vs 其它
      if (err?.errorFields) {
        // 表单校验错误，交由表单显示
      } else if (err?.name === 'BizError') {
        // 如果 errorThrower 抛出的业务错误（我们在 requestErrorConfig 中构造的）
        const bizMsg =
          err?.info?.errorMessage || err?.info?.errorMsg || err?.message || '修改密码失败';
        message.error(bizMsg);
      } else {
        // 其它错误（网络、服务器、解析错误等）
        const msg = err?.message || '修改密码失败，请稍后重试。';
        message.error(msg);
        // 开发环境可打印详细信息（你的 errorConfig 已经会打印）
        if (process.env.NODE_ENV !== 'production') {
          console.error('changePassword error:', err);
        }
      }
    } finally {
      setConfirmLoading(false);
    }
  };


  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;

  if (!currentUser || !currentUser.name) {
    return loading;
  }

  const menuItems = [
    ...(menu
      ? [
          {
            key: 'center',
            icon: <UserOutlined />,
            label: '个人中心',
          },
          {
            key: 'settings',
            icon: <SettingOutlined />,
            label: '个人设置',
          },
          {
            type: 'divider' as const,
          },
        ]
      : []),
    {
      key: 'changePassword',
      icon: <SettingOutlined />,
      label: '修改密码',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  return (
    <>
      <HeaderDropdown
        menu={{
          selectedKeys: [],
          onClick: onMenuClick,
          items: menuItems,
        }}
      >
        {children}
      </HeaderDropdown>
      
      {/* 通知中心组件 */}
      <NotificationCenter />
      
      <Modal
        title="修改密码"
        visible={visible}
        onOk={handleOk}
        onCancel={handleCancel}
        confirmLoading={confirmLoading}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="oldPassword"
            label="当前密码"
            rules={[{ required: true, message: '请输入当前密码' }]}
          >
            <Input.Password placeholder="请输入当前密码" />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码长度至少 6 位' },
            ]}
            hasFeedback
          >
            <Input.Password placeholder="请输入新密码（至少 6 位）" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="确认新密码"
            dependencies={["newPassword"]}
            hasFeedback
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="请再次输入新密码" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};